import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
	convertToInitial, getFormattedInitialPreferencesData, getFormattedPreferencesFields, getPreferencesDetailsSchema,
	PREFERENCES_FORM_FIELDS, PREFERENCES_FORM_FIELDS_NAMES,
	preferencesInitialValues
} from '../../constants/profile-settings/preferences-form';
import { successAlert } from '../ToastMessage';
import moment from 'moment-timezone';
import { isEqual } from 'lodash';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { useTranslation } from 'react-i18next';
import { errorHandler } from '../../utils/newApplicationUtils';
import { SectionWrapper } from '../SectionWrapper';
import { setPreferences } from '../../store/merchnts/actions/security';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { parseJwt } from '../../views/login/Login';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { fetchMerchantData } from '../../store/merchnts/actions/merchant';
import { FormInput } from '../merchants/FormInput';
import { InputType } from '../form/form-input-constants';

const data = {
	languages: [
		{
			value: 'en',
			label: 'English'
		},
		{
			value: 'is',
			label: 'עִבְרִית'
		}
	],
};

export const timeZonesList = moment.tz.names()
	?.map((zoneName) => {
		const offset = moment.tz(zoneName).utcOffset();
		const gmtOffset = offset ? moment.tz(zoneName).format('Z') : '';
		const fullTimezoneName = `(GMT${gmtOffset}) ${zoneName}`;
		return {
			label: fullTimezoneName,
			value: zoneName,
			offset
		};
	})
	.sort((a, b) => a.offset - b.offset);

export const ProfileSettingsPreferences = (): JSX.Element => {
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const user = parseJwt(authUser.idToken);
	const merchantId = user?.['custom:mid'];
	const { t } = useTranslation();
	const { merchantState: { merchantData, isMerchantDataLoading }, dispatch } = useMerchants();

	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	const formik = useFormik({
		initialValues: preferencesInitialValues,
		validationSchema: getPreferencesDetailsSchema,
		onSubmit: () => void 0

	});

	useEffect((): void => {
		const data = convertToInitial(merchantData);
		formik.setValues(data);
	}, [merchantData]);

	const handleEditClick = async (): Promise<void> => {
		formik.handleSubmit();
		if (formik.isValid) {
			try {
				const params = getFormattedPreferencesFields(formik.values);
				const initialData = getFormattedInitialPreferencesData(merchantData);
				if (!isEqual(params, initialData)) {
					setIsLoading(true);
					await setPreferences(params, merchantId);
					await fetchMerchantData(dispatch, merchantId);
					successAlert({
						title: 'Preferences were saved!'
					});
				}
				setIsEditMode(!isEditMode);
				setIsLoading(false);
			} catch (e) {
				console.log(e);
				errorHandler({ e, formik });
				setIsLoading(false);
			}
		}
	};

	const handleCancelClick = (): void => {
		const changedData = getFormattedPreferencesFields(formik.values);
		const initialData = getFormattedInitialPreferencesData(merchantData);
		const dataBeforeChange = convertToInitial(merchantData);
		if (!isEqual(changedData, initialData)) {
			setModalState({
				isOpen: true,
				modalTitle: t('MERCHANT.CANCEL_MODAL_TITLE'),
				modalText: t('MERCHANT.CANCEL_MODAL_TEXT'),
				confirmButtonText: t('MERCHANT.CANCEL_MODAL_CONFIRM_BTN_NAME'),
				onConfirm: closeModal,
				onCancel: (): void => {
					closeModal();
					setIsEditMode(false);
					formik.setValues(dataBeforeChange);
				}
			});
		} else {
			setIsEditMode(false);
		}
	};

	return (
		<>
			<SectionWrapper
				title="Preferences"
				sectionAdditionalClass="mb-5 min-height-200"
				isEditMode={isEditMode}
				setIsEditMode={setIsEditMode}
				onEditClick={handleEditClick}
				onCancelClick={handleCancelClick}
				loading={isLoading}
				pageLoading={isMerchantDataLoading}
				isCardBody
				isEditBlock
			>
				<div className="row">
					<FormInput
						formik={formik}
						fieldName={PREFERENCES_FORM_FIELDS.LANGUAGE}
						label={PREFERENCES_FORM_FIELDS_NAMES.LANGUAGE}
						placeholder={PREFERENCES_FORM_FIELDS_NAMES.LANGUAGE}
						inputType={InputType.Select}
						data={data.languages}
						isEditMode={isEditMode}
						cleanable={false}
						disabled
						key={PREFERENCES_FORM_FIELDS.LANGUAGE}
					/>

					<div className="col-6 col-sm-4 col-md-3"></div>

					<FormInput
						formik={formik}
						fieldName={PREFERENCES_FORM_FIELDS.TIMEZONE}
						label={PREFERENCES_FORM_FIELDS_NAMES.TIMEZONE}
						placeholder={PREFERENCES_FORM_FIELDS_NAMES.TIMEZONE}
						inputType={InputType.Select}
						placement="topEnd"
						data={timeZonesList}
						isEditMode={isEditMode}
						cleanable={false}
						key={PREFERENCES_FORM_FIELDS.TIMEZONE}
					/>
				</div>
			</SectionWrapper>
			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>
		</>
	);
};
